/**
 * Raw schedule data from API for a single day
 * 
 * **Backend API Response Structure**: This is the exact format expected from the backend
 * for each day in the schedule object. The frontend transforms this into ScheduleData format.
 * 
 * @example
 * ```json
 * {
 *   "open": true,           // Whether the store is currently open on this day (calculated server-side)
 *   "closed": false,        // Whether the store is currently closed on this day
 *   "time_range": "10:00 - 21:30",  // Working hours as "HH:MM - HH:MM" string, empty string for day off
 *   "day_off": false        // Explicitly marks if this is a non-working day
 * }
 * ```
 */
export interface RawDaySchedule {
  /** Whether the store is currently open on this day (server-calculated based on current time) */
  open: boolean
  /** Whether the store is currently closed on this day */
  closed: boolean
  /** Working hours string in format "HH:MM - HH:MM" (e.g., "10:00 - 21:30"). Empty string for day off */
  time_range: string
  /** Explicitly marks if this is a non-working day */
  day_off: boolean
}

/**
 * Raw schedule data from API - complete week schedule
 * 
 * **Backend API Response Structure**: This is the exact format expected from the backend
 * when returning filial/store schedule data. Keys should be lowercase day names.
 * 
 * @example
 * ```json
 * {
 *   "monday": { "open": false, "closed": false, "time_range": "10:00 - 21:30", "day_off": false },
 *   "tuesday": { "open": false, "closed": false, "time_range": "10:00 - 21:30", "day_off": false },
 *   "wednesday": { "open": false, "closed": false, "time_range": "10:00 - 21:30", "day_off": false },
 *   "thursday": { "open": true, "closed": false, "time_range": "10:00 - 21:30", "day_off": false },
 *   "friday": { "open": false, "closed": false, "time_range": "10:00 - 21:30", "day_off": false },
 *   "saturday": { "open": false, "closed": false, "time_range": "10:00 - 22:00", "day_off": false },
 *   "sunday": { "open": false, "closed": false, "time_range": "", "day_off": true }
 * }
 * ```
 * 
 * **Important for Backend**:
 * - Keys must be lowercase English day names: monday, tuesday, wednesday, thursday, friday, saturday, sunday
 * - All 7 days must be present
 * - time_range should be empty string or omitted for day_off days
 * - open/closed flags are calculated server-side based on current time in Europe/Kiev timezone
 */
export type RawSchedule = {
  [K in DayName]: RawDaySchedule
}

/**
 * Array of time range strings for a specific day
 * @example ["10:00 - 21:30"] for a working day
 * @example [] for a day off
 * 
 * **Note for Backend**: The Schedule class transforms the API response structure
 * (which contains open/closed/time_range fields) into this simplified array format.
 * Each element is a time range string that will be parsed and displayed in the UI.
 */
export type DaySchedule = string[]

/**
 * Processed schedule data for all days of the week
 * 
 * **Note for Backend**: This is the transformed data after Schedule class processing.
 * The Schedule class accepts raw time_range strings from the API and stores them as arrays.
 * The frontend then uses this to populate the ScheduleTable component.
 */
export type ScheduleData = {
  [K in DayName]: DaySchedule
}

export enum DayName {
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
  SUNDAY = "sunday",
}

/**
 * Complete store schedule object with calculated properties and methods
 * 
 * **Backend Integration Note**: This interface is implemented by the Schedule class (Schedule.js).
 * The backend should provide raw schedule data via the API (filial object with schedule property),
 * and the Schedule class will transform it into this interface.
 * 
 * **Raw API Format Expected**:
 * ```javascript
 * {
 *   "schedule": {
 *     "monday": {
 *       "open": false,          // Is store open right now on this day
 *       "closed": false,        // Is store closed right now on this day
 *       "time_range": "10:00 - 21:30",  // Store hours as string
 *       "day_off": false        // Is it a day off
 *     },
 *     // ... other days
 *   }
 * }
 * ```
 */
export interface StoreSchedule {
  /** Processed schedule data - time ranges organized by day */
  schedule: ScheduleData
  
  /** Current day name in lowercase (e.g., "monday", "tuesday") */
  todayName: DayName | string
  
  /** Tomorrow's day name in lowercase */
  tomorrowName: DayName | string
  
  /** Whether the store is currently open (calculated from current time and today's schedule) */
  isOpen: boolean
  
  /** Next working day with available hours */
  nextWorkingDay?: DayName | string
  
  /** Whether the next working day is tomorrow */
  isNextWorkingDayTomorrow?: boolean
  
  /**
   * Get current time in the store's timezone (Europe/Kiev)
   * @returns Current Date object in store timezone
   * 
   * **Backend Note**: Store uses fixed timezone. Ensure backend times are in consistent timezone.
   */
  currentTime: () => Date
  
  /**
   * Get opening and closing times for a specific day
   * @param day Day name
   * @returns Tuple of [openTime, closeTime] strings or undefined if day off
   * @example ["10:00", "21:30"]
   */
  daySchedule: (day: DayName | string) => [string, string] | undefined
  
  /** Today's opening and closing times if available */
  todaySchedule?: [string, string]
  
  /** Next working day's opening and closing times if available */
  nextWorkingDaySchedule?: [string, string]
}
